import React from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import { toReadOnlyValue } from './AnomalyDetectorForm.utils';
import { enMessages } from '../../../../../../../messages/en-US';

/** Turn a raw ML result key (e.g. "AnomalyScore") into a readable label ("Anomaly Score"). */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
}

interface ReadOnlyFieldProps {
  label: string;
  value: string | number | undefined | null;
}

function ReadOnlyField({ label, value }: ReadOnlyFieldProps): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 text-right break-all">{toReadOnlyValue(value)}</span>
    </div>
  );
}

interface AnomalyMlResultsProps {
  selectedBand: AnomalyBandWithForm | undefined;
}

export default function AnomalyMlResults({
  selectedBand,
}: AnomalyMlResultsProps): React.ReactElement {
  const localizationService = useLocalization();
  const mlResults = selectedBand?.mlResults ?? [];

  if (!selectedBand) {
    return (
      <div className="text-sm text-gray-500">
        {localizationService.toLanguageString('custom.selectAnomalyForMlResults', enMessages.custom.selectAnomalyForMlResults)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {localizationService.toLanguageString('custom.anomaly', enMessages.custom.anomaly)}
        </div>
        <div className="bg-white">
          <ReadOnlyField
            label={localizationService.toLanguageString('custom.anomalyId', enMessages.custom.anomalyId)}
            value={selectedBand?.anomalyId}
          />
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {localizationService.toLanguageString('custom.mlResults', enMessages.custom.mlResults)}
        </div>
        <div className="bg-white">
          {mlResults.length > 0 ? (
            mlResults.map((pair, index) => (
              <ReadOnlyField key={`${pair.k}-${index}`} label={humanizeKey(pair.k)} value={pair.v} />
            ))
          ) : (
            <div className="py-2 text-sm text-gray-500">
              {localizationService.toLanguageString('custom.noMlResults', enMessages.custom.noMlResults)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
